import type { Address } from "viem"
import BigNumber from "bignumber.js"

import type { ThickV1BaseAddresses, ThickV1FeeTier, ThickV1FeeTierInfo } from "./types"

/**
 * Thick-v1 deployment addresses on Base (chainId 8453). This is the current,
 * sole target network for thick-v1. A future thick-v2 domain will own its own
 * deployment map rather than sharing a multi-chain table.
 */
export const THICK_V1_BASE_CHAIN_ID = 8453

export const THICK_V1_BASE_ADDRESSES: ThickV1BaseAddresses = {
  v3CoreFactory: "0xe6da85feb3b4e0d6aed95c41a125fba859bb9d24",
  nfPositionManager: "0x2B52294425a9a229322228de659eDE9D146D7c2f",
  quoter: "0xF5B7181877FeAB982A4b49a99E84BB7cBEBEFF98",
  swapRouter: "0x1F7A12B40bFc8e8561008Bc2ca1FBdc71A36d0e8",
}

/**
 * GraphQL endpoint path used for all thick-v1 reads. The full URL is
 * `${proxyBaseUrl}${THICK_V1_GRAPHQL_ENDPOINT}` where `proxyBaseUrl` is the
 * branch-resolved scale-proxy base. The endpoint path itself is owned by the
 * domain (no constructor injection of the path).
 */
export const THICK_V1_GRAPHQL_ENDPOINT = "/v1/manual-cl/thick-v1/graphql"

/**
 * Canonical fee-tier list for thick-v1 on Base. Mirrors the on-chain
 * Factory.feeAmountTickSpacing() return values and is authoritative for the UI
 * fee-tier picker.
 */
export const THICK_V1_FEE_TIERS: readonly ThickV1FeeTierInfo[] = [
  { key: 500, label: "0.05%", tickSpacing: 10 },
  { key: 3000, label: "0.30%", tickSpacing: 60 },
  { key: 10000, label: "1.00%", tickSpacing: 200 },
] as const

export function getThickV1FeeTierInfo(fee: ThickV1FeeTier): ThickV1FeeTierInfo {
  const found = THICK_V1_FEE_TIERS.find((t) => t.key === fee)
  if (!found) {
    throw new Error(`Unknown thick-v1 fee tier: ${fee}`)
  }
  return found
}

export function getThickV1TickSpacing(fee: ThickV1FeeTier): number {
  return getThickV1FeeTierInfo(fee).tickSpacing
}

/**
 * NFP manager address kept as a top-level export for legacy call-sites that
 * predate `THICK_V1_BASE_ADDRESSES`. The value mirrors
 * `THICK_V1_BASE_ADDRESSES.nfPositionManager`.
 */
export const NFP_MANAGER_ADDRESS: Address = THICK_V1_BASE_ADDRESSES.nfPositionManager

/**
 * Uniswap v3 tick-math constants. Also exported from `manual-cl-core` — kept
 * here for backwards compatibility with call-sites that imported them from
 * the domain folder.
 */
export const TICK_SIZE = 1.0001
export const MIN_TICK = -887272
export const MAX_TICK = 887272

const BASE_FEE_TO_TICKS_MAP = {
  500: 10,
  3000: 60,
  10000: 200,
}

const FANTOM_FEE_TO_TICKS_MAP = {
  100: 1,
  500: 10,
  3000: 60,
  10000: 200,
}

export const getFeeToTicksMapForChain = (chainId: number) => {
  if (chainId === THICK_V1_BASE_CHAIN_ID) {
    return BASE_FEE_TO_TICKS_MAP
  }
  if (chainId === 250) {
    return FANTOM_FEE_TO_TICKS_MAP
  }
  return []
}

const BASE_SUPPORTED_FEE_TIERS = [
  { key: 500, value: "0.05%" },
  { key: 3000, value: "0.30%" },
  { key: 10000, value: "1.00%" },
]

export type FeeTier = {
  key: number
  value: string
}

const FANTOM_SUPPORTED_FEE_TIERS: Array<FeeTier> = [
  { key: 100, value: "0.01%" },
  { key: 500, value: "0.05%" },
  { key: 3000, value: "0.30%" },
  { key: 10000, value: "1.00%" },
]

export const getSupportedFeeTiersForChain = (chainId: number) => {
  if (chainId === THICK_V1_BASE_CHAIN_ID) {
    return BASE_SUPPORTED_FEE_TIERS
  }
  if (chainId === 250) {
    return FANTOM_SUPPORTED_FEE_TIERS
  }
  return []
}

/**
 * Used in liquidity-amount math. BigNumber variant of Uniswap's Q96/Q192
 * JSBI constants.
 * @source https://github.com/Uniswap/sdks/blob/main/sdks/v3-sdk/src/internalConstants.ts#L8-L10
 */
export const Q96 = BigNumber(2).pow(96)
export const Q192 = Q96.pow(2)
